import { useState } from 'react';

import { Button } from '../../button';

import {
  CHANCES,
  failedTrials,
  firstWrongStepText,
  initialPassK,
  initialTable,
  initialTrace,
  initialTrials,
  kindsWithFailures,
  KS,
  orderedRows,
  orderLabels,
  ORDERS,
  passAtFive,
  passAtK,
  passedCount,
  PASSK_REFINEMENT,
  passKSummary,
  passKTransition,
  passPowFive,
  passPowK,
  passPowText,
  percentText,
  gapText,
  readLabel,
  readStopped,
  revealedSteps,
  RUNS,
  runs,
  runTotalsText,
  stepVerdict,
  tableSummary,
  tableTransition,
  toChance,
  toK,
  toOrder,
  toRun,
  toTrace,
  toTrialQuestion,
  TRACE_REFINEMENT,
  traceLabels,
  traceRuns,
  TRACES,
  traceSummary,
  traceTransition,
  TRIAL_QUESTIONS,
  trialQuestionLabels,
  trialRuns,
  TRIALS_REFINEMENT,
  trialsSummary,
  trialsTransition,
  verdict,
  type PassKAction,
  type TableAction,
  type TraceAction,
  type TrialsAction,
} from './agenteval-models';
import { Lab, Panel, Reset, Result } from './lab-parts';
import { ModelSelect } from './learning-model-surface';

import './relationship-labs.css';
import './agenteval-labs.css';

function Predict({ children }: { children: string }) {
  return (
    <p className="rel-predict">
      <strong>Predict first</strong> {children}
    </p>
  );
}
function Table({
  label,
  columns,
  rows,
  template = `repeat(${columns.length}, minmax(0, 1fr))`,
}: {
  label: string;
  columns: string[];
  rows: (string | number)[][];
  template?: string;
}) {
  return (
    <div className="rel-table-scroll">
      <div className="rel-table cb-record-grid" role="table" aria-label={label}>
        <div
          role="row"
          className="rel-row rel-table-head"
          style={{ gridTemplateColumns: template }}
        >
          {columns.map((column) => (
            <span key={column} role="columnheader">
              {column}
            </span>
          ))}
        </div>
        {rows.length ? (
          rows.map((row, index) => (
            <div
              role="row"
              className="rel-row"
              key={index}
              style={{ gridTemplateColumns: template }}
            >
              {row.map((cell, column) => (
                <span role="cell" key={column}>
                  {cell}
                </span>
              ))}
            </div>
          ))
        ) : (
          <div role="row" className="rel-row">
            <span role="cell">No rows</span>
          </div>
        )}
      </div>
    </div>
  );
}
function Facts({ values }: { values: [string, string][] }) {
  return (
    <dl className="cb-memory">
      {values.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
function Bar({ label, value }: { label: string; value: number }) {
  const text = percentText(value);
  return (
    <div>
      <span>{label}</span>
      <div className="agenteval-bar" role="img" aria-label={`${label} ${text}`}>
        <span style={{ width: `${Math.round(value * 100)}%` }} />
      </div>
      <span className="agenteval-value">{text}</span>
    </div>
  );
}
const REPLAY = 'This graphic replays real runs.';
const FORMULA = 'This graphic computes both numbers from the formula.';
const MANY_RUNS = (RUNS as readonly string[]).length > 1;

export function AgentEvalTrialsLab() {
  const [state, setState] = useState(initialTrials);
  const act = (action: TrialsAction) => setState((current) => trialsTransition(current, action));
  const summary = trialsSummary(state);
  return (
    <Lab
      title="How many of five trials pass for this question?"
      eyebrow="Reliability · one task, five trials"
      className="rel-lab agenteval-lab"
    >
      <Predict>
        How many of five trials pass for this question? Choose a Question, then press Run five
        trials.
      </Predict>
      <div className="cb-controls">
        <label>
          Question
          <ModelSelect
            aria-label="Question"
            value={state.question}
            onValueChange={(value) => act({ type: 'question', value: toTrialQuestion(value) })}
            options={TRIAL_QUESTIONS.map((id) => ({ value: id, label: trialQuestionLabels[id] }))}
          />
        </label>
        <Button
          size="sm"
          variant="secondary"
          aria-pressed={state.dataFirst}
          onClick={() => act({ type: 'toggle-data-first' })}
        >
          Grade the data first
        </Button>
        <Button size="sm" disabled={state.run} onClick={() => act({ type: 'run' })}>
          Run five trials
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Table
        label="Five trials"
        columns={['Trial', 'Lookups', 'Graded', 'Answer']}
        template="minmax(0, 0.5fr) minmax(0, 0.7fr) minmax(0, 0.6fr) minmax(0, 2.4fr)"
        rows={
          state.run
            ? trialRuns[state.question].map((entry) => [
                entry.trial,
                entry.tools.length,
                verdict(entry, state.dataFirst),
                entry.answer,
              ])
            : []
        }
      />
      {state.run ? (
        <Facts
          values={[
            ['Passed', `${passedCount(state.question, state.dataFirst)} of 5`],
            ['pass@5', passAtFive(state.question, state.dataFirst)],
            ['pass^5', passPowFive(state.question, state.dataFirst)],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {state.run ? <p className="rel-caption">{TRIALS_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function AgentEvalPassKLab() {
  const [state, setState] = useState(initialPassK);
  const act = (action: PassKAction) => setState((current) => passKTransition(current, action));
  const summary = passKSummary(state);
  const at = passAtK(state.chance, state.k);
  const pow = passPowK(state.chance, state.k);
  return (
    <Lab
      title="With this chance per trial, what are pass@k and pass^k?"
      eyebrow="Reliability · hope against reliability"
      className="rel-lab agenteval-lab"
    >
      <Predict>
        With this chance per trial, what are pass@k and pass^k? Choose a Chance per trial and a
        Trials k, then press Compute.
      </Predict>
      <div className="cb-controls">
        <label>
          Chance per trial
          <ModelSelect
            aria-label="Chance per trial"
            value={String(state.chance)}
            onValueChange={(value) => act({ type: 'chance', value: toChance(value) })}
            options={CHANCES.map((chance) => ({ value: String(chance), label: String(chance) }))}
          />
        </label>
        <label>
          Trials k
          <ModelSelect
            aria-label="Trials k"
            value={String(state.k)}
            onValueChange={(value) => act({ type: 'k', value: toK(value) })}
            options={KS.map((k) => ({ value: String(k), label: String(k) }))}
          />
        </label>
        <Button size="sm" disabled={state.computed} onClick={() => act({ type: 'compute' })}>
          Compute
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="The formula">
        <pre aria-label="The formula">{'pass@k = 1 − (1 − p)^k\npass^k = p^k'}</pre>
      </Panel>
      <Panel label="The two numbers">
        {state.computed ? (
          <div className="agenteval-bars" role="group" aria-label="The two numbers">
            <Bar label={`pass@${state.k}`} value={at} />
            <Bar label={`pass^${state.k}`} value={pow} />
          </div>
        ) : (
          <pre aria-label="The two numbers">(not computed yet)</pre>
        )}
      </Panel>
      {state.computed ? (
        <Facts
          values={[
            [`pass@${state.k}`, percentText(at)],
            [`pass^${state.k}`, percentText(pow)],
            ['Gap', gapText(state.chance, state.k)],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {state.computed ? <p className="rel-caption">{PASSK_REFINEMENT}</p> : null}
      <p className="rel-caption">{FORMULA}</p>
    </Lab>
  );
}

export function AgentEvalTraceLab() {
  const [state, setState] = useState(initialTrace);
  const act = (action: TraceAction) => setState((current) => traceTransition(current, action));
  const run = traceRuns[state.trace];
  const stopped = readStopped(state);
  const summary = traceSummary(state);
  return (
    <Lab
      title="Which step failed first?"
      eyebrow="Reliability · read the trace from the top"
      className="rel-lab agenteval-lab"
    >
      <Predict>Which step failed first? Choose a Trial, then press Read from the top.</Predict>
      <div className="cb-controls">
        <label>
          Trial
          <ModelSelect
            aria-label="Trial"
            value={state.trace}
            onValueChange={(value) => act({ type: 'trace', value: toTrace(value) })}
            options={TRACES.map((id) => ({ value: id, label: traceLabels[id] }))}
          />
        </label>
        <Button size="sm" disabled={stopped} onClick={() => act({ type: 'read' })}>
          {readLabel(state)}
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Table
        label="The trace"
        columns={['Step', 'What happened', 'Right or wrong']}
        template="minmax(0, 0.4fr) minmax(0, 2.2fr) minmax(0, 1.4fr)"
        rows={revealedSteps(state).map((step, index) => [index + 1, step.what, stepVerdict(step)])}
      />
      {stopped ? (
        <Facts
          values={[
            ['First wrong step', firstWrongStepText(run)],
            ['Kind', run.kind],
            ['Who owns the fix', run.owner],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {stopped ? <p className="rel-caption">{TRACE_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function AgentEvalTableLab() {
  const [state, setState] = useState(initialTable);
  const act = (action: TableAction) => setState((current) => tableTransition(current, action));
  const run = runs[state.run];
  const summary = tableSummary(state);
  const facts: [string, string][] = [
    ['Failed trials', String(failedTrials(run))],
    ['Kinds with failures', String(kindsWithFailures(run))],
    [`pass^${run.totals.k}`, passPowText(run)],
  ];
  if (state.cost) facts.push(['Cost per task', run.totals.costPerTask]);
  return (
    <Lab
      title="Which failure kind is tallest?"
      eyebrow="Reliability · one table for failures and cost"
      className="rel-lab agenteval-lab"
    >
      <Predict>
        Which failure kind is tallest? Set Order rows by, then press Build the table.
      </Predict>
      <div className="cb-controls">
        {MANY_RUNS ? (
          <label>
            Run
            <ModelSelect
              aria-label="Run"
              value={state.run}
              onValueChange={(value) => act({ type: 'run', value: toRun(value) })}
              options={RUNS.map((id) => ({ value: id, label: runs[id].label }))}
            />
          </label>
        ) : null}
        <label>
          Order rows by
          <ModelSelect
            aria-label="Order rows by"
            value={state.order}
            onValueChange={(value) => act({ type: 'order', value: toOrder(value) })}
            options={ORDERS.map((order) => ({ value: order, label: orderLabels[order] }))}
          />
        </label>
        <Button
          size="sm"
          variant="secondary"
          aria-pressed={state.cost}
          onClick={() => act({ type: 'toggle-cost' })}
        >
          Show cost per task
        </Button>
        <Button size="sm" disabled={state.built} onClick={() => act({ type: 'build' })}>
          Build the table
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label={run.label}>
        <pre aria-label="The run">{state.built ? runTotalsText(run) : '(not built yet)'}</pre>
      </Panel>
      <Table
        label="First upstream failures"
        columns={['Kind', 'Trials', 'Tasks']}
        template="minmax(0, 1fr) minmax(0, 0.5fr) minmax(0, 1.5fr)"
        rows={
          state.built
            ? orderedRows(run, state.order).map((entry) => [entry.kind, entry.trials, entry.tasks])
            : []
        }
      />
      {state.built ? <Facts values={facts} /> : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {state.built ? <p className="rel-caption">{run.refinement}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}
